import { contentLang, type ShipToAddress } from '@mypoz/shared';

export interface ShipToAddressBlockProps {
  address: ShipToAddress;
  customerCode: string;
}

/**
 * The address itself: the rule-and-inset block that appears once on the Dashboard and once per
 * warehouse on the Warehouses screen (MYP-28). Shared rather than duplicated, because the two
 * have to agree character for character — an address that reads differently in two places is one
 * a customer cannot trust.
 *
 * Two things here are load-bearing rather than decorative:
 *
 * The customer code is real text inside the paragraph, not a pseudo-element or a background
 * label. Plenty of people will drag-select the address rather than press Copy, and a code that
 * only exists as styling would silently not come with them.
 *
 * Nothing truncates. `StatCard` clips its value and that instinct is right for a number; here it
 * would hide part of an address somebody is about to paste into a shipping form.
 */
export function ShipToAddressBlock({ address, customerCode }: ShipToAddressBlockProps) {
  const lang = contentLang(address.country);

  return (
    <div className="border-l-2 border-brand-soft pl-4">
      {/*
        Rendered exactly as received, in whatever script it arrives in — Chinese, Latin, Japanese.
        It stays that way in the English and Malay UIs too: the reader's language has no bearing
        on what a Chinese courier needs to see on the parcel.
      */}
      <p lang={lang} className="text-sm leading-relaxed break-words text-ink">
        {address.addressLine}{' '}
        <span className="rounded bg-brand-soft px-1.5 py-0.5 text-xs font-semibold text-brand">
          {customerCode}
        </span>
      </p>

      {/*
        The code is badged here too, not only on the address above. What the Copy button puts on
        the clipboard attaches it to the receiver name — so a reader who drag-selects instead of
        pressing the button has to be able to see it in the same place, or the two paths disagree
        about the one field the warehouse matches a parcel on.

        Joined from parts so a warehouse missing a phone does not render a trailing separator.
      */}
      <p lang={lang} className="mt-2 text-sm break-words text-muted">
        {address.recipient}
        {address.recipient ? (
          <span className="mx-1 rounded bg-brand-soft px-1.5 py-0.5 text-xs font-semibold text-brand">
            {customerCode}
          </span>
        ) : null}
        {address.recipient && address.phone ? ' · ' : ''}
        {address.phone}
      </p>
    </div>
  );
}
